import { Box, CardMedia, Typography, useTheme } from "@mui/material"
import { Trans } from "react-i18next"
import { useTranslation } from "providers/i18n"
import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { IMultiLanguageInfo } from "utils/feature"
import _ from "lodash"

interface IDirectionSummaryProps {
  duration: number
  distance: number
  originName: string | IMultiLanguageInfo
  destinationName: string | IMultiLanguageInfo
}

const DEFAULT_MAX_LOCATION_NAME_LENGTH = 20

export const DirectionSummary = ({
  duration,
  distance,
  originName,
  destinationName,
}: IDirectionSummaryProps) => {
  const { t } = useTranslation()

  const theme = useTheme()

  const {
    originVenueLogo,
    destinationVenueLogo,
    originVenueData,
    destinationVenueData,
  } = useDirectionOverview()

  const originVenueRef = _.get(originVenueData, "reference")
  const destinationVenueRef = _.get(destinationVenueData, "reference")

  const {
    primaryText,
    highlightedText,
    directionSummaryContainer,
    locationSummaryContainer,
    locationContainer,
    venueLogoContainer,
    venueLogo,
    startText,
    endText,
    locationText,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const { maxLocationName } =
    theme.components[`AppDirectionOverview`]?.defaultProps || {}

  const highlightedStepText = (
    <Typography
      className="AppDirectionOverview-highlightedText"
      component="span"
      sx={highlightedText}
    />
  )

  /**
   * dynamic line count base on `originName.length + destinationName.length`
   * - if total length is less than or equal to 20 character → 1 line
   * - if total length is greater than 20 character → 2 lines
   *
   * this can also be configured via `AppDirectionOverview.defaultProps.maxLocationName.length` in theme
   */
  const locationNameLength =
    (t(originName)?.length ?? 0) + (t(destinationName)?.length ?? 0)
  const isLongLocationName =
    locationNameLength > (maxLocationName || DEFAULT_MAX_LOCATION_NAME_LENGTH)

  return (
    <Box sx={directionSummaryContainer}>
      <Typography variant="title" component="h1" sx={primaryText} gutterBottom>
        <Trans
          components={{
            duration: highlightedStepText,
            distance: highlightedStepText,
          }}
        >
          {t("Travel", {
            duration,
            distance,
          })}
        </Trans>
      </Typography>

      <Box
        sx={locationSummaryContainer}
        className={isLongLocationName ? "is-long-location-name" : ""}
      >
        <Box sx={locationContainer}>
          <Typography sx={startText}>{t("from")}</Typography>
          <Typography variant="body2" sx={locationText}>
            {t(originName)}
          </Typography>
          {originVenueLogo && (
            <Box sx={venueLogoContainer} className={originVenueRef}>
              <CardMedia image={originVenueLogo} sx={venueLogo} />
            </Box>
          )}
        </Box>
        <Box sx={locationContainer}>
          <Typography sx={endText}>{t("to")}</Typography>
          <Typography variant="body2" sx={locationText}>
            {t(destinationName)}
          </Typography>
          {destinationVenueLogo && (
            <Box sx={venueLogoContainer} className={destinationVenueRef}>
              <CardMedia image={destinationVenueLogo} sx={venueLogo} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
